import { ColumnsType } from "antd/es/table";
import { ColumnType } from "antd/lib/table";
import { VisibleIcon, NotVisibleIcon } from "assets/icons";
import TableRowField from "components/fields/TableRowField";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { namePathOrMatcher } from "utils/formUtils";
import { renderTableAmountColumn } from "utils/tableUtils";

import { TicketPoolListFieldValue } from "../../types";

import { UseTicketPoolsTable } from "./types";

const useTicketPoolsTableColumns = (
  actionsColumn?: ColumnType<TicketPoolListFieldValue>,
  asSignUpOptions?: boolean
) => {
  const intl = useIntl();

  return useMemo<ColumnsType<TicketPoolListFieldValue>>(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Name",
        }),
        dataIndex: "name",
      },
      ...(asSignUpOptions
        ? []
        : [
            {
              title: intl.formatMessage({
                defaultMessage: "Face Value Price",
              }),
              dataIndex: "price",
              render: renderTableAmountColumn(),
            },
            {
              title: intl.formatMessage({
                defaultMessage: "Venue Fee",
              }),
              dataIndex: "venueFee",
              render: renderTableAmountColumn(),
            },
          ]),
      {
        title: intl.formatMessage({
          defaultMessage: "QTY",
        }),
        dataIndex: "quantity",
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Max Tickets Per Order",
        }),
        dataIndex: "maxTicketsPerOrder",
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Min Tickets Per Order",
        }),
        dataIndex: "minTicketsPerOrder",
      },
      ...(asSignUpOptions
        ? [
            {
              title: intl.formatMessage({
                defaultMessage: "Auto Approve?",
              }),
              dataIndex: "isAutoApprove",
              render: (isAutoApprove: boolean) =>
                isAutoApprove
                  ? intl.formatMessage({ defaultMessage: "Yes" })
                  : intl.formatMessage({ defaultMessage: "No" }),
            },
          ]
        : [
            {
              title: intl.formatMessage({
                defaultMessage: "On Sale Date",
              }),
              dataIndex: "availableBeforeDays",
            },
            {
              title: intl.formatMessage({
                defaultMessage: "On Sale Time",
              }),
              dataIndex: "availableBeforeTime",
            },
            {
              title: intl.formatMessage({
                defaultMessage: "Expiration Date",
              }),
              dataIndex: "expireBeforeDays",
            },
            {
              title: intl.formatMessage({
                defaultMessage: "Expiration Time",
              }),
              dataIndex: "expireBeforeTime",
            },
            {
              title: intl.formatMessage({
                defaultMessage: "Available in Widget",
              }),
              dataIndex: "isScannerOnly",
              render: (isScannerOnly: boolean) =>
                isScannerOnly ? <NotVisibleIcon /> : <VisibleIcon />,
            },
          ]),
      ...(actionsColumn ? [actionsColumn] : []),
    ],
    [intl, asSignUpOptions, actionsColumn]
  );
};

export const useTicketPoolsTable: UseTicketPoolsTable = ({
  actionsColumn,
  dataSource,
  asSignUpOptions,
  ...props
}) => {
  const columns = useTicketPoolsTableColumns(actionsColumn, asSignUpOptions);

  return {
    ...props,
    columns,
    dataSource,
    components: { body: { row: TableRowField } },
    onRow(_data, index) {
      if (index === undefined) {
        return {};
      }

      return {
        matcher: namePathOrMatcher({
          equals: [["input", "ticketPools"]],
          startsWith: [["input", "ticketPools", index]],
        }),
      } as React.HTMLAttributes<unknown>;
    },
  };
};
